// src/app/routes.tsx
import * as React from 'react';
import { Dashboard } from '@app/Dashboard/Dashboard';
import { Countries } from './Administration/Countries/Countries';
import { Languages } from './Administration/Languages/Languages';
import { ClassDetail } from './Classes/ClassDetail';
import { ClassSurveyPage } from './Classes/ClassSurveyPage';
import { ClassesOverview } from './Classes/Classes';
import { ClassesHistory } from './Classes/History';
import { PipelineOverview } from './Classes/Pipeline';
import { PipelineDetail } from './Classes/PipelineDetail';
import { CourseDetail } from './Courses/CourseDetail';
import { Courses } from './Courses/Courses';
import { Facilities } from './Facilities/Facilities';
import { FacilityDetail } from './Facilities/FacilitiesDetail';
import { CustomFields } from './Instructors/CustomFields';
import { InstructorDetail } from './Instructors/InstructorDetail';
import { Instructors } from './Instructors/Instructors';
import { Utilization } from './Instructors/Utilization';
import { TSSOverview } from './TSS/TSS';
import { TSSDetail } from './TSS/TSSDetail';
import { ClientExecutiveOverview } from './ClientExecutive/ClientExecutives';
import { ClientExecutiveDetail } from './ClientExecutive/ClientExecutiveDetail';
import { RedHatRegions } from './Administration/RedHatRegion/RedHatRegion';
import { GeneralSettings } from '@app/Administration/General/GeneralSettings';
import { PartnerOverview } from './Partners/Partners';
import { PartnerDetail } from './Partners/PartnerDetail';
import { Communications } from './Administration/Communications/Communications';
import { AutomationMessages } from './Administration/Automation/AutomationMessage';
import { FlagsOverview } from './Administration/FlagsDefinition/Flags';
import { FlagDetail } from './Administration/FlagsDefinition/FlagDetail';
import { Calendar } from './Classes/Calendar';
import { Holidays } from './Administration/Holidays/Holidays';
import { PostalCodes } from './Administration/PostalCodes/PostalCodes';
import { PrivateCourses } from './Courses/PrivateCourses';
import { AutomationProcesses } from './Administration/Automation/AutomationProcesses';
import { Activity } from './Activity/Activity';
import { RequireRole } from './components/RequireRole';
import { ROLE } from './utils/roles';

export interface AppRouteConfig {
  path?: string;
  element?: React.ReactElement;
  title?: string;
  label?: string;
  routes?: AppRouteConfig[];
  /**
   * Realm roles allowed here; holding any one of them is enough. Left out means every
   * authenticated user, which is the case for all but the Administration section.
   *
   * Set on a group and it covers every route under it, including ones added later - which is
   * the point of declaring it here rather than in the pages. A child that names its own roles
   * replaces what it inherits rather than adding to it, so narrowing a single page inside an
   * open group works, and so does the mistake of widening one: check the group before setting
   * this on a child.
   *
   * Two things read it. flattenRoutes wraps the element in RequireRole, so the address answers
   * with the forbidden page instead of the route; AppLayout leaves the navigation item out. Both
   * are presentation - the endpoints behind these pages enforce the same roles themselves, and
   * README-permissions.md is the list of which.
   */
  roles?: readonly string[];
}

export const routes: AppRouteConfig[] = [
  { element: <Dashboard />, path: '/', title: 'CAS | Dashboard', label: 'Dashboard' },
  {
    label: "Classes",
    routes: [
      { element: <ClassesOverview />, path: '/classes', title: 'CAS | Classes', label: 'Classes' },
      { element: <ClassDetail />, path: '/classes/:oid', title: 'Class Detail' },
      // Every answer on the class's survey, by question and by respondent. Its own page rather
      // than a tab because a submission names the student who made it, so the whole of it is
      // admin's - the half that is everybody's is the Survey tab on the class page itself.
      // `roles` here is what makes the address answer with the forbidden page: reached from the
      // tab and from the class page's kebab, neither of which offers the link without the role.
      { element: <ClassSurveyPage />, path: '/classes/:oid/survey', title: 'CAS | Class Survey', roles: [ROLE.ADMIN] },
      { element: <Calendar />, path: '/classes/calendar', title: 'CAS | Classes - Calendar', label: 'Calendar' },
      // Requests that precede a class. A static segment, so it is matched ahead of /classes/:oid
      // above - the same arrangement calendar and history already rely on.
      { element: <PipelineOverview />, path: '/classes/pipeline', title: 'CAS | Classes - Pipeline', label: 'Pipeline' },
      { element: <PipelineDetail />, path: '/classes/pipeline/:id', title: 'Pipeline Request' },
      { element: <ClassesHistory />, path: '/classes/history', title: 'CAS | Classes - History', label: 'History' },
      // The same detail page, reached from History: read only, and on its own path so the
      // breadcrumb and the layout's Back button lead back to the search the user came from
      // rather than to the Classes list.
      { element: <ClassDetail isHistory />, path: '/classes/history/:oid', title: 'Class Detail' },
      // The same page, reached from a class opened out of History, so that Back and the breadcrumb
      // lead to the search the reader came from rather than to the editable class.
      { element: <ClassSurveyPage />, path: '/classes/history/:oid/survey', title: 'CAS | Class Survey', roles: [ROLE.ADMIN] },
    ]
  },
  { label: 'Courses',
    routes: [
      { element: <Courses />, path: '/courses', title: 'CAS | Courses', label: 'Courses' },
      // The create form, on its own path so that it can be guarded without guarding the detail
      // page beside it: a course is everybody's to read and an administrator's to write. A
      // static segment, so it is matched ahead of /courses/:sku below - the same arrangement
      // the calendar and the pipeline already rely on. The list offers the button on the same
      // condition, so nobody is handed a door that does not open.
      { element: <CourseDetail isNew />, path: '/courses/new', title: 'CAS | Courses', roles: [ROLE.ADMIN] },
      { element: <CourseDetail />, path: '/courses/:sku', title: 'CAS | Courses' },
      { element: <PrivateCourses />, path: '/privatecourses', title: 'CAS | Private Courses', label: 'Private Courses' },

    ]
  },
  {
    label: 'Instructors',
    routes: [
      { element: <Instructors />, path: '/instructors', title: 'CAS | Instructors', label: 'Instructors List' },
      { element: <InstructorDetail />, path: '/instructors/:id', title: 'CAS | Instructors' },
      { element: <CustomFields />, path: '/customfields', title: 'CAS | Instructor - Custom Fields', label: 'Custom Fields' },
      // Derived from classes rather than held anywhere, so it keeps no store and every search is
      // a request - see the page. No `roles`: the endpoint behind it is @Authenticated and
      // nothing more, like the rest of this group.
      { element: <Utilization />, path: '/instructors/utilization', title: 'CAS | Instructor - Utilization', label: 'Utilization' }
    ]
  },
  // Outside Administration on purpose: the audit feed is open to any authenticated user, and it
  // reports revisions to types the reader may not follow as the type name alone. Putting it under
  // the admin group would hide from everybody else a page they are entitled to.
  { element: <Activity />, path: '/activity', title: 'CAS | Activity', label: 'Activity' },
  { element: <ClientExecutiveOverview />, path: '/clientexecutives', title: 'CAS | Client Executives', label: 'Client Executives' },
  { element: <ClientExecutiveDetail />, path: '/clientexecutives/:id', title: 'CAS | Client Executives' },
  { element: <PartnerOverview />, path: '/partners', title: 'CAS | Partners', label: 'Partners' },
  { element: <PartnerDetail />, path: '/partners/:id', title: 'CAS | Partners' },
  { element: <Facilities />, path: '/facilities', title: 'CAS | Facilities', label: 'Facilities' },
  { element: <FacilityDetail />, path: '/facilities/:id', title: 'CAS | Facilities' },
  { element: <TSSOverview />, path: '/tss', title: 'CAS | Training Specialist Strategic', label: 'TSS' },
  { element: <TSSDetail />, path: '/tss/:id', title: 'CAS | Training Specialist Strategic' },
  {
    label: 'Administration',
    // The whole section, in one place: reference data, the runtime settings, the flag rules and
    // the automation triggers are all an administrator's. Inherited by every route below,
    // so adding a page here needs nothing else.
    roles: [ROLE.ADMIN],
    routes: [
      {
        label: 'Automation',
        routes: [
          { element: <AutomationProcesses />, path: '/admin/automation/processes', title: 'CAS | Administration - Automation Processes', label: 'Automation Processes' },
          { element: <AutomationMessages />, path: '/admin/automation/messages', title: 'CAS | Administration - Automation Messages', label: 'Automation Messages' }
        ]
      },
      { element: <Communications />, path: '/admin/communications', title: 'CAS | Administration - Communications', label: 'Communications' },
      { element: <Countries />, path: '/admin/countries', title: 'CAS | Administration - Countries', label: 'Countries' },
      { element: <FlagsOverview />, path: '/admin/flags', title: 'CAS | Administration - Flags', label: 'Flags' },
      { element: <FlagDetail />, path: '/admin/flags/:id', title: 'CAS | Administration - Flags'},
      { element: <GeneralSettings />, path: '/admin/general', title: 'CAS | Administration - General Settings', label: 'General Settings' },
      { element: <Holidays />, path: '/admin/holidays', title: 'CAS | Administration - Holidays', label: 'Holidays' },
      { element: <Languages />, path: '/admin/languages', title: 'CAS | Administration - Languages', label: 'Languages' },
      { element: <PostalCodes />, path: '/admin/postalcodes', title: 'CAS | Administration - Postal Codes', label: 'Postal Codes' },
      { element: <RedHatRegions />, path: '/admin/redhatregions', title: 'CAS | Administration - Red Hat Regions', label: 'Red Hat Regions' }
    ],
  },
];

/**
 * Puts the route's element behind its roles, so an address that is not this user's answers with
 * the forbidden page rather than the page itself.
 *
 * Done here, on the way out of the tree, rather than route by route: every element the router
 * gets has been through this, so a page added to a restricted group is guarded by having been
 * put there and cannot be forgotten. A route with no roles is handed back untouched.
 */
const guard = (route: AppRouteConfig, roles?: readonly string[]): AppRouteConfig => {
  if (!roles || roles.length === 0 || !route.element) return { ...route, roles };
  return { ...route, roles, element: <RequireRole roles={roles}>{route.element}</RequireRole> };
};

// Flatten the routes for the router config, carrying each group's roles down to its children
const flattenRoutes = (routesArray: AppRouteConfig[], inherited?: readonly string[]): AppRouteConfig[] => {
  return routesArray.reduce((acc, route) => {
    // A group's roles cover everything under it; a child naming its own replaces them.
    const roles = route.roles ?? inherited;

    // 1. If it's just a group/folder (has routes, but no path), skip it and just flatten its children
    if (!route.path && route.routes) {
      return [...acc, ...flattenRoutes(route.routes, roles)];
    }

    // 2. If a route happens to have both a path AND nested routes
    if (route.path && route.routes) {
      return [...acc, guard(route, roles), ...flattenRoutes(route.routes, roles)];
    }

    // 3. It's a standard page route, just add it to the array
    return [...acc, guard(route, roles)];
  }, [] as AppRouteConfig[]);
};

export const flattenedRoutes = flattenRoutes(routes);